import { useState, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/hooks/useAuth';
import { useProgressAssessments } from '@/hooks/useProgressAssessments';
import { useSchoolFilters } from '@/hooks/useSchoolFilters';
import {
  getGradeDistribution,
  getAvgGradeTrend,
  getGradeScatterSeries,
  getFluencyAverages,
  getFluencyTrend,
  groupByChildName,
  getLatestAssessment,
  getOverallGrade,
  normaliseGrade,
  GRADE_NUM_LABEL,
} from '@/lib/progress';
import GradeBadge from '@/components/GradeBadge';
import { MarkdownText } from '@/components/MarkdownText';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BookOpen, ChevronDown, ChevronUp, Copy, Check, Download, FileText, Loader2 } from 'lucide-react';
import { buildCsv, downloadCsv, gradeLabel, isoDate, todayStamp } from '@/lib/exportCsv';
import { supabase } from '@/integrations/supabase/client';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { SavedAssessment } from '@/lib/progress';

type GradeType = 'working_towards' | 'expected' | 'greater_depth';

function isValidGrade(grade: string): grade is GradeType {
  return ['working_towards', 'expected', 'greater_depth'].includes(grade);
}

// Matches app design tokens used in GradeBadge
const GRADE_COLORS: Record<string, string> = {
  'Working Towards': 'hsl(var(--grade-working-towards))',
  'Expected': 'hsl(var(--grade-expected))',
  'Greater Depth': 'hsl(var(--grade-greater-depth))',
};

const FLUENCY_COLORS = {
  'Syntactic Maturity': 'hsl(var(--grade-greater-depth))',
  'Textual Cohesion': 'hsl(var(--info))',
  'Lexical Precision': 'hsl(var(--warning))',
};

const GRADE_FULL_LABEL: Record<string, string> = {
  working_towards: 'Working Towards Standard',
  expected: 'Expected Standard',
  greater_depth: 'Greater Depth',
};

const GRADE_SHORT: Record<string, string> = {
  working_towards: 'WTS',
  expected: 'ES',
  greater_depth: 'GD',
};

// ---- Scope helpers -----------------------------------------------------------

type ReportScope = 'this-term' | 'this-year' | 'all';

function getScopeLabel(scope: ReportScope): string {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  if (scope === 'this-term') {
    if (m >= 9) return `Autumn ${y}`;
    if (m <= 3) return `Spring ${y}`;
    return `Summer ${y}`;
  }
  if (scope === 'this-year') {
    const sy = m >= 9 ? y : y - 1;
    return `${sy}–${String(sy + 1).slice(2)}`;
  }
  return 'All available assessments';
}

function getPageScopeLabel(filterPeriod: string): string {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  if (filterPeriod === 'all') return 'All time';
  if (filterPeriod === '7') return 'Last 7 days';
  if (filterPeriod === '30') return 'Last 30 days';
  if (filterPeriod === '90') return 'Last 90 days';
  if (filterPeriod === 'this-term') {
    if (m >= 9) return `Autumn ${y}`;
    if (m <= 3) return `Spring ${y}`;
    return `Summer ${y}`;
  }
  if (filterPeriod === 'this-year') {
    const sy = m >= 9 ? y : y - 1;
    return `${sy}–${String(sy + 1).slice(2)}`;
  }
  return '';
}

function applyReportScope(assessments: SavedAssessment[], scope: ReportScope): SavedAssessment[] {
  const now = new Date();
  const m = now.getMonth() + 1;
  const y = now.getFullYear();
  if (scope === 'all') return assessments;
  if (scope === 'this-term') {
    const start = m >= 9 ? new Date(y, 8, 1) : m <= 3 ? new Date(y, 0, 1) : new Date(y, 3, 1);
    return assessments.filter((a) => new Date(a.created_at) >= start);
  }
  if (scope === 'this-year') {
    const start = new Date(m >= 9 ? y : y - 1, 8, 1);
    return assessments.filter((a) => new Date(a.created_at) >= start);
  }
  return assessments;
}

// ---- Evidence builder -------------------------------------------------------

function buildReportEvidence(
  assessments: SavedAssessment[],
  scopeLabel: string,
) {
  const sorted = [...assessments].sort((a, b) => a.created_at.localeCompare(b.created_at));

  const grade_pattern_summary = sorted
    .map((a) => {
      const g = normaliseGrade(a.grade);
      const short = GRADE_SHORT[g] ?? '?';
      const d = new Date(a.created_at);
      const mon = d.toLocaleString('en-GB', { month: 'short' });
      const yr = String(d.getFullYear()).slice(2);
      return `${short} (${mon} '${yr})`;
    })
    .join(' → ') || 'Not available';

  const overallGrade = getOverallGrade(assessments);
  const overall_grade = overallGrade ? GRADE_FULL_LABEL[overallGrade] : 'Not available';

  const fluencyAvgs = getFluencyAverages(assessments);
  const fluency_summary =
    fluencyAvgs.length > 0
      ? fluencyAvgs.map((f) => `${f.metric}: ${f.value}/10`).join(', ')
      : 'Not available';

  // Combine assessment texts, strip markdown, split into two halves for themes
  const combined = sorted
    .map((a) =>
      a.assessment_text
        .replace(/\*\*/g, '')
        .replace(/^[-*#]\s/gm, '')
        .replace(/\n+/g, ' ')
        .trim(),
    )
    .join(' ')
    .trim();

  const strength_themes = combined.slice(0, 500) || 'Not available';
  const development_themes = combined.length > 500 ? combined.slice(500, 900) : 'Not available';

  return {
    pupil_name: assessments[0]?.child_name ?? '',
    year_group: assessments[0]?.year_group ?? '',
    scope_label: scopeLabel,
    assessment_count: assessments.length,
    overall_grade,
    grade_pattern_summary,
    fluency_summary,
    strength_themes,
    development_themes,
  };
}

// ---- Component --------------------------------------------------------------

export default function Progress() {
  const { user, profile, hasRole, planTier, billingStatus } = useAuth();
  const canGenerateReport = planTier !== 'free' && billingStatus !== 'trialing';

  const isAdmin = hasRole('school_admin') || hasRole('slt');

  const {
    data: assessments = [],
    isLoading: loading,
    isError: hasError,
  } = useProgressAssessments(user?.id, isAdmin);

  const { data: schoolFilters } = useSchoolFilters(profile?.school_id);

  const [mode, setMode] = useState<'grades' | 'fluency'>('grades');
  const [filterPeriod, setFilterPeriod] = useState<string>('all');
  const [filterKeyStage, setFilterKeyStage] = useState<string>('all');
  const [filterYearGroup, setFilterYearGroup] = useState<string>('all');
  const [filterClass, setFilterClass] = useState<string>('all');
  const [filterPupil, setFilterPupil] = useState<string>('all');

  // Report generation state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPupilName, setReportPupilName] = useState<string>('');
  const [reportScope, setReportScope] = useState<ReportScope>('this-term');
  const [generating, setGenerating] = useState(false);
  const [reportComment, setReportComment] = useState('');
  const [reportError, setReportError] = useState('');
  const [copied, setCopied] = useState(false);

  // Expand/collapse state for history panels
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  // Per-assessment read-more state
  const [expandedTexts, setExpandedTexts] = useState<Set<string>>(new Set());

  // ---- Date filter helper --------------------------------------------------

  const applyDateFilter = (dateStr: string): boolean => {
    if (filterPeriod === 'all') return true;
    const date = new Date(dateStr);
    const now = new Date();
    if (filterPeriod === '7' || filterPeriod === '30' || filterPeriod === '90') {
      const cutoff = new Date(now);
      cutoff.setDate(cutoff.getDate() - parseInt(filterPeriod));
      return date >= cutoff;
    }
    if (filterPeriod === 'this-term') {
      const m = now.getMonth() + 1;
      const y = now.getFullYear();
      const start = m >= 9 ? new Date(y, 8, 1) : m <= 3 ? new Date(y, 0, 1) : new Date(y, 3, 1);
      return date >= start;
    }
    if (filterPeriod === 'this-year') {
      const m = now.getMonth() + 1;
      const y = now.getFullYear();
      const start = new Date(m >= 9 ? y : y - 1, 8, 1);
      return date >= start;
    }
    return true;
  };

  // ---- Derived data --------------------------------------------------------

  // Base filter: period + key stage + year group + class
  const filteredAssessments = useMemo(() => {
    return assessments.filter((a) => {
      if (!applyDateFilter(a.created_at)) return false;
      if (filterKeyStage !== 'all' && (a.key_stage_name ?? '') !== filterKeyStage) return false;
      if (filterYearGroup !== 'all' && a.year_group !== filterYearGroup) return false;
      if (filterClass !== 'all' && (a.class_name ?? '') !== filterClass) return false;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assessments, filterPeriod, filterKeyStage, filterYearGroup, filterClass]);

  // Pupil options derived from the base filter (respects class/year/ks filters)
  const pupilOptions = useMemo(() => {
    const names = [...new Set(filteredAssessments.map((a) => a.child_name))];
    return names.sort((a, b) => a.localeCompare(b));
  }, [filteredAssessments]);

  // Final display set: base filter + optional pupil filter
  const displayAssessments = useMemo(() => {
    if (filterPupil === 'all') return filteredAssessments;
    return filteredAssessments.filter((a) => a.child_name === filterPupil);
  }, [filteredAssessments, filterPupil]);

  // All assessments for the pupil currently targeted by the report dialog
  const pupilAllAssessments = useMemo(() => {
    if (!reportPupilName) return [];
    return assessments.filter((a) => a.child_name === reportPupilName);
  }, [assessments, reportPupilName]);

  const isPupilSelected = filterPupil !== 'all';

  const overallGrade = useMemo(
    () => getOverallGrade(displayAssessments),
    [displayAssessments],
  );

  const gradeDistribution = useMemo(
    () => getGradeDistribution(displayAssessments),
    [displayAssessments],
  );

  const avgGradeTrend = useMemo(
    () => getAvgGradeTrend(displayAssessments),
    [displayAssessments],
  );

  const gradeScatterSeries = useMemo(
    () => getGradeScatterSeries(displayAssessments),
    [displayAssessments],
  );

  const fluencyAverages = useMemo(
    () => getFluencyAverages(displayAssessments),
    [displayAssessments],
  );

  const fluencyTrend = useMemo(
    () => getFluencyTrend(displayAssessments),
    [displayAssessments],
  );

  const groups = useMemo(
    () => groupByChildName(displayAssessments),
    [displayAssessments],
  );

  // ---- Expand/collapse helpers ---------------------------------------------

  const toggleGroup = (childName: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(childName)) {
        next.delete(childName);
        setExpandedTexts((prevTexts) => {
          const nextTexts = new Set(prevTexts);
          (groups[childName] ?? []).forEach((a) => nextTexts.delete(a.id));
          return nextTexts;
        });
      } else {
        next.add(childName);
      }
      return next;
    });
  };

  const toggleText = (id: string) => {
    setExpandedTexts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ---- CSV export ----------------------------------------------------------

  const handleExportCsv = () => {
    const headers = [
      'pupil_name', 'year_group', 'class_name', 'key_stage',
      'term', 'assessment_date', 'grade',
    ];
    const rows = displayAssessments.map((a) => [
      a.child_name,
      a.year_group,
      a.class_name ?? '',
      a.key_stage_name ?? '',
      a.term ?? '',
      isoDate(a.created_at),
      gradeLabel(a.grade),
    ]);
    downloadCsv(`progress_${todayStamp()}.csv`, buildCsv(headers, rows));
  };

  // ---- Report generation ---------------------------------------------------

  const handleOpenReport = (pupilName: string) => {
    setReportPupilName(pupilName);
    setReportComment('');
    setReportError('');
    setCopied(false);
    setReportOpen(true);
  };

  const handleGenerate = async () => {
    const scopeAssessments = applyReportScope(pupilAllAssessments, reportScope);
    if (scopeAssessments.length === 0) return;

    setGenerating(true);
    setReportError('');
    setReportComment('');
    setCopied(false);

    try {
      const evidence = buildReportEvidence(scopeAssessments, getScopeLabel(reportScope));
      const { data, error } = await supabase.functions.invoke('generate-report-comment', {
        body: evidence,
      });
      if (error) throw error;
      setReportComment(data?.comment ?? '');
    } catch (err: unknown) {
      setReportError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!reportComment) return;
    await navigator.clipboard.writeText(reportComment);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ---- Render --------------------------------------------------------------

  const noAssessmentsGlobally = !loading && !hasError && assessments.length === 0;
  const hasAssessments = !loading && !hasError && assessments.length > 0;
  const hasFluencyData = fluencyAverages.length > 0;

  // Assessments in scope for the report dialog
  const reportScopeAssessments = useMemo(
    () => applyReportScope(pupilAllAssessments, reportScope),
    [pupilAllAssessments, reportScope],
  );

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h1 className="text-3xl font-bold text-foreground">Pupil Progress</h1>
          {hasAssessments && displayAssessments.length > 0 && (
            <Button variant="outline" size="sm" onClick={handleExportCsv}>
              <Download className="h-4 w-4 mr-1.5" />
              Export CSV
            </Button>
          )}
        </div>

        {/* Filter bar */}
        {hasAssessments && (
          <div className="flex flex-wrap gap-2">
            <Select value={filterPeriod} onValueChange={setFilterPeriod} disabled={loading}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All time" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="this-term">This term</SelectItem>
                <SelectItem value="this-year">This academic year</SelectItem>
              </SelectContent>
            </Select>
            {(schoolFilters?.keyStages?.length ?? 0) > 0 && (
              <Select value={filterKeyStage} onValueChange={setFilterKeyStage} disabled={loading}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All key stages" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All key stages</SelectItem>
                  {schoolFilters!.keyStages.map((ks) => (
                    <SelectItem key={ks.id} value={ks.name}>{ks.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(schoolFilters?.yearGroups?.length ?? 0) > 0 && (
              <Select value={filterYearGroup} onValueChange={setFilterYearGroup} disabled={loading}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All year groups" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All year groups</SelectItem>
                  {schoolFilters!.yearGroups.map((yg) => (
                    <SelectItem key={yg.id} value={yg.name}>{yg.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {(schoolFilters?.classes?.length ?? 0) > 0 && (
              <Select value={filterClass} onValueChange={setFilterClass} disabled={loading}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="All classes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All classes</SelectItem>
                  {schoolFilters!.classes.map((cl) => (
                    <SelectItem key={cl.id} value={cl.name}>{cl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {pupilOptions.length > 0 && (
              <Select value={filterPupil} onValueChange={setFilterPupil} disabled={loading}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="All pupils" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All pupils</SelectItem>
                  {pupilOptions.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {/* Error */}
        {!loading && hasError && (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Failed to load progress data. Please refresh.
            </CardContent>
          </Card>
        )}

        {/* Global empty */}
        {noAssessmentsGlobally && (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <BookOpen className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No assessments yet.</p>
              <p className="text-sm mt-1">
                Run an assessment on the Upload page.
              </p>
            </CardContent>
          </Card>
        )}

        {/* No results for current filters */}
        {hasAssessments && displayAssessments.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No assessments found for the selected filters.
          </p>
        )}

        {/* Selected-pupil summary card */}
        {isPupilSelected && displayAssessments.length > 0 && (
          <Card className="shadow-soft border-primary/20 bg-muted/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="space-y-1.5">
                  <p className="font-semibold text-lg leading-none">{filterPupil}</p>
                  {displayAssessments[0]?.year_group && (
                    <p className="text-sm text-muted-foreground">
                      {displayAssessments[0].year_group}
                      {displayAssessments[0]?.class_name && ` · ${displayAssessments[0].class_name}`}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap pt-0.5">
                    <span className="text-sm text-muted-foreground">
                      {displayAssessments.length} {displayAssessments.length === 1 ? 'assessment' : 'assessments'}
                      {filterPeriod !== 'all' && ` · ${getPageScopeLabel(filterPeriod)}`}
                    </span>
                    {overallGrade && isValidGrade(overallGrade) && (
                      <GradeBadge grade={overallGrade} size="sm" />
                    )}
                  </div>
                </div>
                {canGenerateReport && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenReport(filterPupil)}
                    className="shrink-0"
                  >
                    <FileText className="h-4 w-4 mr-1.5" />
                    Generate report comment
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Mode toggle + charts */}
        {hasAssessments && displayAssessments.length > 0 && (
          <>
            <Tabs value={mode} onValueChange={(v) => setMode(v as 'grades' | 'fluency')}>
              <TabsList>
                <TabsTrigger value="grades">Grades</TabsTrigger>
                <TabsTrigger value="fluency" disabled={!hasFluencyData}>
                  Fluency{!hasFluencyData ? ' (no data)' : ''}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* ---- GRADES MODE ---- */}
            {mode === 'grades' && (
              <div className="space-y-6">
                {/* Grade distribution bar */}
                <Card className="shadow-soft">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Grade Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={gradeDistribution}
                        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                      >
                        <XAxis dataKey="grade" tick={{ fontSize: 12 }} />
                        <YAxis
                          allowDecimals={false}
                          label={{
                            value: 'Assessments',
                            angle: -90,
                            position: 'insideLeft',
                            offset: 10,
                            style: { fontSize: 12 },
                          }}
                          tick={{ fontSize: 12 }}
                          width={80}
                        />
                        <Tooltip />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {gradeDistribution.map((entry) => (
                            <Cell
                              key={entry.grade}
                              fill={GRADE_COLORS[entry.grade] ?? 'hsl(var(--primary))'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Average grade over time (only if >1 data point) */}
                {avgGradeTrend.length > 1 && (
                  <Card className="shadow-soft">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">
                        {isPupilSelected ? 'Grade Progression' : 'Average Grade Over Time'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart
                          data={avgGradeTrend}
                          margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis
                            type="number"
                            domain={[0.5, 3.5]}
                            ticks={[1, 2, 3]}
                            tickFormatter={(v: number) => GRADE_NUM_LABEL[v] ?? ''}
                            tick={{ fontSize: 12 }}
                            width={40}
                          />
                          <Tooltip
                            formatter={(value: number) => [
                              GRADE_NUM_LABEL[Math.round(value)] ?? value.toFixed(1),
                              isPupilSelected ? 'Grade' : 'Avg Grade',
                            ]}
                          />
                          <Line
                            type="monotone"
                            dataKey="avg"
                            stroke="hsl(var(--primary))"
                            strokeWidth={2}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Assessment timeline scatter — cohort view only */}
                {!isPupilSelected && displayAssessments.length > 0 && (
                  <Card className="shadow-soft">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Assessment Timeline</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={240}>
                        <ScatterChart margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis
                            dataKey="x"
                            type="number"
                            domain={['auto', 'auto']}
                            tickFormatter={(v: number) => {
                              const d = new Date(v);
                              return `${d.toLocaleString('default', { month: 'short' })} '${String(d.getFullYear()).slice(2)}`;
                            }}
                            tick={{ fontSize: 11 }}
                            name="Date"
                          />
                          <YAxis
                            dataKey="y"
                            type="number"
                            domain={[0.5, 3.5]}
                            ticks={[1, 2, 3]}
                            tickFormatter={(v: number) => GRADE_NUM_LABEL[v] ?? ''}
                            tick={{ fontSize: 12 }}
                            width={40}
                          />
                          <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={({ payload }) => {
                              if (!payload?.length) return null;
                              const d = payload[0].payload;
                              return (
                                <div className="rounded border bg-popover p-2 text-xs shadow">
                                  <p className="font-medium">{d.name}</p>
                                  <p className="text-muted-foreground">
                                    {new Date(d.x).toLocaleDateString('en-GB')} · {GRADE_NUM_LABEL[d.y]}
                                  </p>
                                </div>
                              );
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {gradeScatterSeries.map((s) => (
                            <Scatter
                              key={s.label}
                              name={s.label}
                              data={s.data}
                              fill={GRADE_COLORS[s.label]}
                              opacity={0.85}
                            />
                          ))}
                        </ScatterChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* ---- FLUENCY MODE ---- */}
            {mode === 'fluency' && hasFluencyData && (
              <div className="space-y-6">
                {/* Fluency averages bar */}
                <Card className="shadow-soft">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Average Fluency Scores</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart
                        data={fluencyAverages}
                        margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                        layout="vertical"
                      >
                        <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 12 }} />
                        <YAxis
                          dataKey="metric"
                          type="category"
                          tick={{ fontSize: 11 }}
                          width={140}
                        />
                        <Tooltip />
                        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                          {fluencyAverages.map((entry) => (
                            <Cell
                              key={entry.metric}
                              fill={FLUENCY_COLORS[entry.metric as keyof typeof FLUENCY_COLORS] ?? 'hsl(var(--primary))'}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Fluency trend line chart */}
                {fluencyTrend.length > 1 && (
                  <Card className="shadow-soft">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg">Fluency Trend Over Time</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={260}>
                        <LineChart
                          data={fluencyTrend}
                          margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                          <YAxis
                            domain={[0, 10]}
                            tick={{ fontSize: 12 }}
                            width={36}
                            label={{
                              value: 'Score',
                              angle: -90,
                              position: 'insideLeft',
                              offset: 10,
                              style: { fontSize: 12 },
                            }}
                          />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          {(
                            ['Syntactic Maturity', 'Textual Cohesion', 'Lexical Precision'] as const
                          ).map((metric) => (
                            <Line
                              key={metric}
                              type="monotone"
                              dataKey={metric}
                              stroke={FLUENCY_COLORS[metric]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                              connectNulls={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </>
        )}

        {/* Pupil group cards */}
        {hasAssessments &&
          Object.entries(groups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([childName, childAssessments]) => {
              const latest = getLatestAssessment(childAssessments);
              const isExpanded = expandedGroups.has(childName);
              const count = childAssessments.length;

              return (
                <Card key={childName} className="shadow-soft">
                  <CardContent className="pt-4 pb-2">
                    {/* Summary row */}
                    <div className="flex items-center gap-3 flex-wrap pb-2">
                      <span className="font-semibold">{childName}</span>
                      {latest?.year_group && (
                        <span className="text-sm text-muted-foreground">
                          {latest.year_group}
                        </span>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {count} {count === 1 ? 'assessment' : 'assessments'}
                      </span>
                      {latest?.grade && isValidGrade(latest.grade) && (
                        <GradeBadge grade={latest.grade} size="sm" />
                      )}
                      {latest?.term && (
                        <span className="text-xs text-muted-foreground">{latest.term}</span>
                      )}
                    </div>

                    {canGenerateReport && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="shrink-0 ml-auto"
                        onClick={() => handleOpenReport(childName)}
                      >
                        <FileText className="h-4 w-4 mr-1.5" />
                        Generate report comment
                      </Button>
                    )}

                    {/* History toggle */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-between text-sm"
                      onClick={() => toggleGroup(childName)}
                    >
                      {isExpanded ? 'Hide history' : 'Show history'}
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>

                    {/* Expanded history */}
                    {isExpanded && (
                      <div className="mt-2 space-y-3 border-t pt-3">
                        {childAssessments.map((a) => {
                          const isTextExpanded = expandedTexts.has(a.id);
                          const plainText = a.assessment_text
                            .replace(/\*\*/g, '')
                            .replace(/^[-*]\s/gm, '')
                            .replace(/\n+/g, ' ')
                            .trim();
                          const isLong = plainText.length > 100;

                          return (
                            <div key={a.id} className="text-sm space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-muted-foreground">
                                  {new Date(a.created_at).toLocaleDateString('en-GB')}
                                </span>
                                {a.grade && isValidGrade(a.grade) && (
                                  <GradeBadge grade={a.grade} size="sm" />
                                )}
                              </div>
                              {isTextExpanded ? (
                                <MarkdownText variant="compact">
                                  {a.assessment_text}
                                </MarkdownText>
                              ) : (
                                <p className="text-muted-foreground leading-relaxed text-sm">
                                  {plainText.slice(0, 100)}{isLong && '…'}
                                </p>
                              )}
                              {isLong && (
                                <button
                                  className="text-primary text-xs underline underline-offset-2"
                                  onClick={() => toggleText(a.id)}
                                >
                                  {isTextExpanded ? 'Show less' : 'Read more'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
      </div>

      {/* Report comment dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generate Report Comment</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* Pupil + scope */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-medium text-sm">{reportPupilName}</span>
              <Select
                value={reportScope}
                onValueChange={(v) => {
                  setReportScope(v as ReportScope);
                  setReportComment('');
                  setReportError('');
                }}
              >
                <SelectTrigger className="w-48 h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="this-term">This term</SelectItem>
                  <SelectItem value="this-year">This academic year</SelectItem>
                  <SelectItem value="all">All available</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assessment count in scope */}
            <p className="text-sm text-muted-foreground">
              {reportScopeAssessments.length === 0
                ? 'No assessments in this scope.'
                : `${reportScopeAssessments.length} ${reportScopeAssessments.length === 1 ? 'assessment' : 'assessments'} in scope · ${getScopeLabel(reportScope)}`}
            </p>

            {/* Generate button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || reportScopeAssessments.length === 0}
              className="w-full"
            >
              {generating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating…
                </>
              ) : (
                'Generate report comment'
              )}
            </Button>

            {/* Error */}
            {reportError && (
              <p className="text-sm text-destructive">{reportError}</p>
            )}

            {/* Generated comment */}
            {reportComment && (
              <div className="space-y-2">
                <div className="rounded-md border bg-muted/40 p-3 text-sm leading-relaxed">
                  {reportComment}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-1.5 text-success" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-1.5" />
                      Copy comment
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
